import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';
import { addWeeks } from 'date-fns';

export default Factory.extend({
  type: 'UserDocuments::Visitor',

  id: (i) => `vis-${i + 1}`,

  createdAt: () => faker.date.recent(),
  updatedAt: () => faker.date.recent(),
  skipped: false,

  locationIds: () => [],
  entryIds: () => [],
  inviteIds: () => [],
  recurringInviteIds: () => [],

  asIdentificationCard: trait({
    afterCreate(visitorDocument, server) {
      const userDocumentTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'identification-card' }) ??
        server.create('user-document-template', 'asIdentificationCard');
      const inputFieldsData = visitorDocument?.inputFieldsData;
      const expiration_date =
        inputFieldsData?.issue_date ?? faker.date.future({ years: 1, refDate: visitorDocument.createdAt });

      visitorDocument.update({
        userDocumentTemplateId: userDocumentTemplate.id,
        inputFieldsData: {
          ...inputFieldsData,
          expiration_date,
        },
      });
    },
  }),

  withLocation: trait({
    afterCreate(visitorDocument, server) {
      if (!visitorDocument.location) {
        const [location] = (server.schema.companies.first() ?? server.create('company', 'withLocation')).locations
          .models;

        visitorDocument.update({ locations: [location] });
      }
    },
  }),

  withAttachments: trait({
    afterCreate(visitorDocument, server) {
      const userDocumentTemplateAttachments =
        visitorDocument?.userDocumentTemplate?.userDocumentTemplateAttachments?.models || [];

      userDocumentTemplateAttachments.forEach((userDocumentTemplateAttachment) => {
        server.create('user-document-attachment', {
          visitorDocument,
          userDocumentTemplateAttachment,
        });
      });
    },
  }),

  withExternalAttachments: trait({
    afterCreate(visitorDocument) {
      visitorDocument.update({
        externalAttachments: [
          {
            type: 'back',
            url: 'https://picsum.photos/600/600',
          },
          {
            type: 'front',
            url: 'https://picsum.photos/600/600',
          },
        ],
      });
    },
  }),

  asApproved: trait({
    afterCreate(visitorDocument, server) {
      server.create('user-document-link', 'asApproved', {
        userDocument: visitorDocument,
        createdAt: visitorDocument.createdAt,
      });
    },
  }),

  asDenied: trait({
    afterCreate(visitorDocument, server) {
      server.create('user-document-link', 'asDenied', {
        userDocument: visitorDocument,
        createdAt: visitorDocument.createdAt,
      });
    },
  }),

  asReview: trait({
    afterCreate(visitorDocument, server) {
      server.create('user-document-link', 'asReview', {
        userDocument: visitorDocument,
        createdAt: visitorDocument.createdAt,
      });
    },
  }),

  asCovidTestResult: trait({
    afterCreate(visitorDocument, server) {
      const userDocumentTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-test-result' }) ??
        server.create('user-document-template', 'asCovidTestResult');
      const inputFieldsData = visitorDocument?.inputFieldsData;
      const issueDate =
        inputFieldsData?.issue_date ?? faker.date.past({ years: 1, refDate: visitorDocument.createdAt }).toUTCString();

      visitorDocument.update({
        userDocumentTemplateId: userDocumentTemplate.id,
        inputFieldsData: {
          ...inputFieldsData,
          issue_date: issueDate,
        },
      });
    },
  }),

  asCovidVaccineCard: trait({
    afterCreate(visitorDocument, server) {
      const userDocumentTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-vaccine-card' }) ??
        server.create('user-document-template', 'asCovidVaccineCard');
      const inputFieldsData = visitorDocument?.inputFieldsData;
      const lastDoseDate =
        inputFieldsData?.last_dose_date ??
        faker.date.past({ years: 1, refDate: addWeeks(visitorDocument.createdAt, -2) }).toUTCString();

      visitorDocument.update({
        userDocumentTemplateId: userDocumentTemplate.id,
        inputFieldsData: {
          ...inputFieldsData,
          last_dose_date: lastDoseDate,
        },
      });
    },
  }),

  withAnInvite: trait({
    afterCreate(visitorDocument, server) {
      const [location] = visitorDocument.locations.models;

      visitorDocument.update({
        invites: server.createList('invite', 1, { location }),
      });
    },
  }),

  withAnEntry: trait({
    afterCreate(visitorDocument, server) {
      const [location] = visitorDocument.locations.models;

      visitorDocument.update({
        entries: server.createList('entry', 1, { locationId: location.id }),
      });
    },
  }),

  withRecurringInvite: trait({
    afterCreate(visitorDocument, server) {
      const [location] = visitorDocument.locations.models;
      const recurringInvites = server.createList('recurring-invite', 1, { location });
      const [recurringInvite] = recurringInvites;
      const { flow, startTime } = recurringInvite;
      const userData = [{ field: 'Purpose of visit', value: flow.name }];

      for (let i = 0; i < 5; i++) {
        const expectedArrivalTime = addWeeks(startTime, i);

        server.create('invite', { location, recurringInvite, expectedArrivalTime, flowName: flow.name, userData });
      }

      visitorDocument.update({ recurringInvites });
    },
  }),
});
